import { Novel, ScenePack, SceneSnapshot } from '@/lib/types';
import {
  deleteDoc,
  deleteField,
  doc,
  DocumentData,
  FieldValue,
  getDoc,
  setDoc,
  Transaction,
  UpdateData,
  updateDoc
} from 'firebase/firestore';
import { BaseDao, stripUndefined } from './base-dao';

/**
 * Specialized DAO for Narrative elements (Chapters, Scenes, Arcs).
 * Handles structured narrative data and automatic metadata mirroring.
 */
export class NarrativeDao extends BaseDao {
  private readonly SUMMARY_FIELDS = [
    'id', 'name', 'order', 'type', 'preview', 'updatedAt', 'chapterId', 'eventIds', 'characterIds', 'locationIds', 'organizationIds', 'status', 'isArchive', 'povCharacterId', 'shortDescription', 'mainContentId', 'contentVariants', 'contentVariantVersions', 'publishStatus', 'chapterPublicId', 'publishedContentId', 'compiledSceneVersions', 'variantSceneRefs'
  ];

  /**
   * Updates a narrative item and automatically mirrors specified summary fields.
   */
  async updateNarrativeItem(
    userId: string,
    novel: Novel,
    type: string,
    itemId: string,
    updates: UpdateData<DocumentData> | FieldValue,
    transaction?: Transaction
  ): Promise<void> {

    const finalUpdates = { ...(updates as any), updatedAt: new Date().toISOString() };
    await this.updateItem(userId, novel, 'narrative', type, itemId, finalUpdates, transaction);
    await this.updateNarrativeSummary(userId, novel, type, itemId, finalUpdates, transaction);
  }

  /**
   * Sets (upserts) a narrative item and mirrors summary fields.
   */
  async setNarrativeItem<T extends { id: string }>(
    userId: string,
    novel: Novel,
    type: string,
    item: T,
    transaction?: Transaction
  ): Promise<void> {
    const finalItem = { ...item, updatedAt: new Date().toISOString() };
    await this.setItem(userId, novel, 'narrative', type, finalItem, transaction);
    await this.updateNarrativeSummary(userId, novel, type, item.id, finalItem, transaction);
  }

  /**
   * Internal helper to update the mirror in the narrative summaries bucket.
   */
  private async updateNarrativeSummary(
    userId: string,
    novel: Novel,
    type: string,
    itemId: string,
    data: DocumentData,
    transaction?: Transaction
  ): Promise<void> {
    const summaryUpdates: Record<string, unknown> = {};
    Object.entries(data).forEach(([key, value]) => {
      if (this.SUMMARY_FIELDS.includes(key)) {
        const cleanValue = stripUndefined(value);
        if (cleanValue !== undefined) {
          summaryUpdates[key] = cleanValue;
        }
      }
    });

    if (Object.keys(summaryUpdates).length === 0) return;

    const { ref, fieldPath } = this.resolver.resolveSummary(userId, novel.id, 'narrative', type, itemId, novel.storageLayout);
    if (!fieldPath) return;

    const payload: Record<string, unknown> = {};
    Object.entries(summaryUpdates).forEach(([key, value]) => {
      payload[`${fieldPath}.${key}`] = value;
    });

    if (transaction) {
      transaction.update(ref, payload as UpdateData<DocumentData>);
    } else {
      try {
        await updateDoc(ref, payload as UpdateData<DocumentData>);
      } catch (e) {
        try {
          const nested = this.nestPayload(fieldPath, summaryUpdates);
          await setDoc(ref, nested, { merge: true });
        } catch (setErr) {
          console.warn(`Summary update failed for Narrative ${itemId} of type ${type}.`, setErr);
        }
      }
    }
  }

  /**
   * Specifically for Scenes, which include extra aggregate data.
   */
  async updateScenePack(
    userId: string,
    novel: Novel,
    sceneId: string,
    scene: Partial<ScenePack>,
    transaction?: Transaction
  ): Promise<void> {
    return this.updateNarrativeItem(userId, novel, 'scenes', sceneId, scene as UpdateData<DocumentData>, transaction);
  }

  /**
   * Deletes a narrative item and removes its reference from summaries.
   */
  async deleteNarrativeItem(
    userId: string,
    novel: Novel,
    type: string,
    itemId: string,
    transaction?: Transaction
  ): Promise<void> {
    await this.deleteItem(userId, novel, 'narrative', type, itemId, transaction);

    const { ref, fieldPath } = this.resolver.resolveSummary(userId, novel.id, 'narrative', type, itemId, novel.storageLayout);
    if (fieldPath) {
      const updates = { [fieldPath]: deleteField() };
      if (transaction) {
        transaction.update(ref, updates as UpdateData<DocumentData>);
      } else {
        await updateDoc(ref, updates as UpdateData<DocumentData>);
      }
    }

    if (type === 'arcs') {
      const detailsRef = doc(this.firestore, `users/${userId}/novels/${novel.id}/arcs/${itemId}/details/main`);
      if (transaction) {
        transaction.delete(detailsRef);
      } else {
        try {
          await deleteDoc(detailsRef);
        } catch (err) {
          console.warn(`Failed to delete details sub-document for arc ${itemId}`, err);
        }
      }
    }
  }

  async saveSceneSnapshot(
    userId: string,
    novelId: string,
    sceneId: string,
    snapshot: SceneSnapshot
  ): Promise<void> {
    const ref = doc(this.firestore, `users/${userId}/novels/${novelId}/scenes/${sceneId}/snapshots/current`);
    await setDoc(ref, snapshot);
  }

  async getSceneSnapshot(
    userId: string,
    novelId: string,
    sceneId: string,
    snapshotId: string
  ): Promise<SceneSnapshot | null> {
    const ref = doc(this.firestore, `users/${userId}/novels/${novelId}/scenes/${sceneId}/snapshots/current`);
    const snap = await getDoc(ref);
    return snap.exists() ? (snap.data() as SceneSnapshot) : null;
  }
}
